import { Component, HostListener, OnInit, inject, input, signal, viewChild } from "@angular/core";
import { DocumentsRequestedRibbonService } from "./ribbon.service";
import { DoNotPropagateClickStandaloneDirective } from "@mipro/directives/do-not-propagate-click.directive";
import { OverlayPanel, OverlayPanelModule } from "primeng/overlaypanel";
import { MiproSharedModule } from "@mipro/mipro-shared.module";
import { DatePipe } from "@angular/common";
import { ScrollPanelModule } from "primeng/scrollpanel";
import { CustomFileUploadFn } from "@mipro/classes/custom-file-upload-fn";
import { DocumentRequest } from "./request";

@Component({
    selector: "documents-requested-ribbon",
    standalone: true,
    imports: [OverlayPanelModule, MiproSharedModule, DatePipe, ScrollPanelModule],
    hostDirectives: [DoNotPropagateClickStandaloneDirective],
    styleUrl: "./ribbon.component.scss",
    template: `
        @if (service.refreshing$$()) {
            <div class="documents-requested-ribbon">
                <m-loading [inline]="true" diameterPx="15px" />
            </div>
        }

        @if (service.hasRequests$$()) {
            <div class="documents-requested-ribbon flex align-items-center gap-1" [class.error]="service.uploadError$$()">
                <i class="pi pi-file"></i>
                <span>
                    @if (service.documentRequestsCount$$() <= 1) {
                        A Document has been requested
                    } @else {
                        Documents have been requested
                    }
                </span>
                @if (service.loadingRequests$$()) {
                    <m-loading [inline]="true" diameterPx="15px" />
                }
            </div>

            <p-overlayPanel styleClass="p-2">
                <p-scrollPanel [style]="{ width: '100%' }">
                    <div class="flex flex-column gap-3 documents-requested-overlay">
                        @for (request of service.documentRequests$$(); track request.id) {
                            <div class="documents-requested-overlay-item-content px-3 py-1" (click)="onRequestClick(request)">
                                <p class="request-message">{{ request.message }}</p>
                                <p class="request-metadata ">
                                    Requested on {{ request.createdAt | date: "dd/MM/yyyy" }} 
                                    @if(!request.currentPersonIsRequester) {
                                        by {{ request.requesterFullName }}
                                    }
                                </p>
                            </div>
                        }
                    </div>
                </p-scrollPanel>
            </p-overlayPanel>

            <m-documents-dialog
                [(visible)]="documentDialogVisible$$"
                [customFileUploadMethod]="uploadFile"
                [style]="{ 'max-width': '800px', width: '100%' }"
                [showDateSelector]="true"
                parentEntityName="Episode">
            </m-documents-dialog>
        }
    `
})
export class DocumentsRequestedRibbonComponent implements OnInit {
    readonly episodeId$$ = input.required<string>({ alias: "episodeId" });

    protected readonly service = inject(DocumentsRequestedRibbonService);
    protected readonly documentDialogVisible$$ = signal(false);

    private readonly overlay = viewChild(OverlayPanel);
    private uploadingFilesForRequestId: string | null = null;

    ngOnInit(): void {
        this.service.initialize(this.episodeId$$());
    }

    @HostListener("click", ["$event"])
    protected async onClick(event: PointerEvent) {
        await this.service.getDocumentRequests();
        this.overlay().toggle(event);
    }

    protected onRequestClick(request: DocumentRequest) {
        if(request.currentPersonIsRecipient || request.currentPersonIsRequester) {
            this.showUploadDialog(request.id);
        }
    }

    private showUploadDialog(requestId: string) {
        this.uploadingFilesForRequestId = requestId;
        this.documentDialogVisible$$.set(true);
    }

    protected uploadFile: CustomFileUploadFn = (documentType, uploadDateTime, files) => {
        return this.service.upload({
            requestId: this.uploadingFilesForRequestId,
            documentType,
            uploadDateTime,
            files
        });
    };
}
